import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useBusPartnersCostYears} from './useBusPartnersCostYears';

interface UseBusPartnerCostsQueryParamsResult {
  busPartnerId: string | null;
  handleChangeBusPartnerId: (busPartnerId: string | null) => void;
  year: number | null;
  handleChangeYear: (year: number | null) => void;
}

export const useBusPartnerCostsQueryParams =
  (): UseBusPartnerCostsQueryParamsResult => {
    const {busPartnersCostYears} = useBusPartnersCostYears();

    const busPartnerIdToYears = useMemo(
      () =>
        (busPartnersCostYears ?? []).reduce((map, {busPartnerId, years}) => {
          map.set(busPartnerId, years);
          return map;
        }, new Map<string, number[]>()),
      [busPartnersCostYears]
    );

    const [busPartnerId, setBusPartnerId] = useState<string | null>(null);
    const [year, setYear] = useState<number | null>(null);

    const location = useLocation();
    const navigate = useNavigate();

    const updateUrlParams = useCallback(
      (newBusPartnerId: string | null, newYear: number | null) => {
        const params = new URLSearchParams(location.search);

        if (newBusPartnerId) params.set('busPartnerId', newBusPartnerId);
        else params.delete('busPartnerId');

        if (newYear != null) params.set('year', String(newYear));
        else params.delete('year');

        navigate(`?${params.toString()}`, {replace: false});
      },
      [location.search, navigate]
    );

    const handleChangeBusPartnerId = useCallback(
      (newBusPartnerId: string | null) => {
        // try to keep already selected year if it exists in the new bus partner
        let newYear: number | null = null;
        if (
          newBusPartnerId &&
          year &&
          busPartnerIdToYears.get(newBusPartnerId)?.includes(year)
        ) {
          newYear = year;
        }

        updateUrlParams(newBusPartnerId, newYear);
      },
      [year, busPartnerIdToYears, updateUrlParams]
    );

    const handleChangeYear = useCallback(
      (newYear: number | null) => {
        updateUrlParams(busPartnerId, newYear);
      },
      [busPartnerId, updateUrlParams]
    );

    // Sync URL -> state and reset invalid params
    useEffect(() => {
      if (!busPartnerIdToYears.size) return;

      const params = new URLSearchParams(location.search);

      const busPartnerId = params.get('busPartnerId');
      if (busPartnerId && !busPartnerIdToYears.has(busPartnerId)) {
        // reset both bus partner and year
        navigate(`?`, {replace: true});
        return;
      }

      const year = params.has('year') ? Number(params.get('year')) : null;
      if (year) {
        if (
          !busPartnerId ||
          !busPartnerIdToYears.get(busPartnerId)?.includes(year)
        ) {
          // reset year
          params.delete('year');
          navigate(`?${params.toString()}`, {replace: true});
          return;
        }
      }

      setBusPartnerId(busPartnerId);
      setYear(year);
    }, [busPartnerIdToYears, location.search, navigate]);

    return {busPartnerId, handleChangeBusPartnerId, year, handleChangeYear};
  };
